import { FormEvent, useRef, useState, useEffect } from "react";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import { InputFieldOTP } from "../../components/ui/Inputs";
import { PrimaryButton, SecondaryButtonText } from "../../components/ui/Button";
import { useSelector, useDispatch } from "react-redux";
import Toast from "../../components/ui/Toast";
import { ROUTES } from "../../routes/routerConfig";
import { useLocation, useNavigate } from "react-router-dom";
import {
  setOtpVerifyUsersData,
  setUserProfileData,
  setSetPasswordKey,
  setSetPasswordArray,
  setResetToken,
  authSelector,
  setUserAuth,
  setUserPermission,
  setUserBusinessType,
  setUserLoanCategory,
  setTriggerData,
} from "../../services/redux/slices/authSlice";
import service from "../../services/api/service";
import { setStatusPopup } from "../../services/redux/slices/statusPopupSlice";
import {
  loginTypeData,
  maskEmail,
  maskEmailOrMobile,
  maskMobile,
} from "../../utils/helpers";
import ResendOtpTimer from "../../components/common/ResendOtpTimer";
import { decrypt, encrypt } from "../../utils/crypto";
import ButtonLoader from "../../components/ui/ButtonLoader";
import ReactGA from "react-ga4";
import { addSignupOnboardApi } from "../../services/redux/slices/keyRegistrarSlice";
import LoaderBackdrop from "../../components/ui/LoaderBackdrop";
import {
  setDocumentApplicantType,
  setIsBypassLogin,
  setTrackingId,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { useTriggerData } from "../../hooks/useTriggerData";

type props = {
  setNextStep: (step: string) => void;
};

const Otp = ({ setNextStep }: props) => {
  const numberOfDigits = 6;
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [otp, setOtp] = useState<string[]>(new Array(numberOfDigits)?.fill(""));
  const inputRefs = useRef<HTMLInputElement[]>([]);
  const [severity, setSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("success");
  const [isLoading, setIsLoading] = useState(false);
  const [isCoApplicantOtpLoading, setIsCoApplicantOtpLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "error" | "warning" | "info" | "success"
  >("success");
  const handleSnackbarClose = () => setSnackbarOpen(false);
  const handleSnackbarOpen = (message: string, severity: string) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity as "error" | "warning" | "info" | "success");
    setSnackbarOpen(true);
  };
  const registerKey = useSelector((state: any) => state.keyRegistrar.keys);
  const mobileNumbers = useSelector(
    (state: any) => state.keyRegistrar.mobileNumbers
  );
  const lanNumbers = useSelector((state: any) => state.keyRegistrar.lanNumbers);
  const loginType = useSelector((state: any) => state.keyRegistrar.loginType);

  const { isTriggerDataEmpty, triggerData }: any = useTriggerData();

  const handleClearedOtp = () => {
    setOtp(new Array(numberOfDigits).fill(""));
  };

  const handleOTPChange = (index: number, value: string) => {
    let newOTP = [...otp];
    const numericValue = value?.replace(/\D/g, "");
    const singleDigit = numericValue.slice(0, 1);
    if (singleDigit || value === "") {
      newOTP[index] = singleDigit;

      if (singleDigit && index < numberOfDigits - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
    setOtp(newOTP);
  };

  const handleOtpReset = () => {
    setOtp(new Array(numberOfDigits)?.fill(""));
    inputRefs.current[0].focus();
  };

  const handleBackspaceAndEnter = (e: any, index: number) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputRefs.current[index - 1].focus();
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (otp.every((val) => val !== "")) {
        isTriggerDataEmpty
          ? handleSubmit(e)
          : triggerData?.toUser
            ? handleSubmit(e)
            : handleVerifyCoApplicantOtp();
      }
    }
  };

  const handleVerifyCoApplicantOtp = async () => {
    setIsLoading(true);

    const body = {
      id: encrypt(triggerData?.coApplicantMobileNumber),
      otp: encrypt(otp.join("")),
      trackingId: triggerData?.submissionTrackingId,
    };
    try {
      const res = await service?.auth?.verifyCoApplicantOtp(body);
      const coApplicantOtpVerifyData = res?.data?.data;
      if (isTriggerDataEmpty) {
        dispatch(setTrackingId(coApplicantOtpVerifyData?.trackingId));
      }
      const oldtriggerData = {
        ...triggerData,
        coApplicantType: coApplicantOtpVerifyData?.coApplicantType,
      };
      dispatch(setTriggerData(oldtriggerData));
      dispatch(
        setDocumentApplicantType(coApplicantOtpVerifyData?.coApplicantType)
      );

      // Call bypass API after successful OTP verification
      const encryptedKey = encrypt(oldtriggerData?.applicantMobileNumber);
      const bypassResponse = await service?.auth?.bypassLogin({
        key: encryptedKey,
        key2: encrypt(triggerData?.coApplicantMobileNumber),
        role: "coApplicant",
        trackingId: triggerData?.submissionTrackingId,
      });

      if (bypassResponse?.data?.status === "success") {
        const data: any = JSON.parse(decrypt(bypassResponse?.data?.data));
        dispatch(setUserProfileData(data?.user));
        dispatch(setUserPermission(data?.permission));
        dispatch(
          setUserBusinessType(
            data?.businessType?.length > 0 ? data?.businessType?.[0] : {}
          )
        );
        dispatch(setUserLoanCategory(data?.result));
        dispatch(setUserAuth(data?.token));
        dispatch(setIsBypassLogin(true));

        setIsLoading(false);
      } else {
        setIsLoading(false);
        dispatch(
          setStatusPopup({
            status: "error",
            message: bypassResponse?.data?.message || "Bypass login failed",
            isFunction: true,
            locationFunction: handleClearedOtp,
          })
        );
      }
    } catch (error: any) {
      setSeverity("error");
      const errorMessage = error.response?.data?.message || "An error occurred";
      setIsLoading(false);
      dispatch(
        setStatusPopup({
          status: "error",
          message: errorMessage,
          isFunction: true,
          locationFunction: handleClearedOtp,
        })
      );
    }
  };

  const { otpUserData } = useSelector(authSelector);

  const userOtpMessage = (userData: any) => {

    const baseMessage = "Please enter the OTP sent to your";
    if (userData?.loginType === "cif") {
      return (
        <div>
          {baseMessage}
          {userData?.email && (
            <>
              {" "}
              email{" "}
              <span
                style={{
                  color: "#1CB1B1",
                  textDecoration: "none",
                  WebkitTextDecorationLine: "none",
                }}
                className="text-disable wordbreak"
              >
                {maskEmail(String(userData?.email))}
              </span>
            </>
          )}
          {userData?.email && userData?.mobile && <> and </>}
          {userData?.mobile && (
            <>
              {" "}
              mobile{" "}
              <span
                style={{
                  color: "#1CB1B1",
                  textDecoration: "none !important",
                  WebkitTextDecorationLine: "none !important",
                }}
                className="text-disable"
              >
                {maskMobile(String(userData?.mobile))}
              </span>
            </>
          )}
        </div>
      );
    } else if (userData?.loginType === "phone") {
      return (
        <div>
          {baseMessage} mobile{" "}
          <span
            style={{
              color: "#1CB1B1",
              textDecoration: "none !important",
              WebkitTextDecorationLine: "none !important",
            }}
            className="text-disable"
          >
            {maskMobile(String(userData?.mobile))}
          </span>
        </div>
      );
    } else if (userData?.loginType === "email") {
      return (
        <div>
          {baseMessage} email{" "}
          <span
            style={{
              color: "#1CB1B1",
              textDecoration: "none !important",
              WebkitTextDecorationLine: "none !important",
            }}
            className="text-disable"
          >
            {maskEmail(String(userData?.email))}
          </span>
        </div>
      );
    } else if (registerKey == "register") {
      return (
        <div>
          {baseMessage} {loginType}
          <span
            style={{
              color: "#1CB1B1",
              textDecoration: "none",
              WebkitTextDecorationLine: "none",
            }}
            className="text-disable wordbreak"
          >
            {" "}
            (<>{maskEmailOrMobile(mobileNumbers)}</>)
          </span>
        </div>
      );
    } else {
      return <div>{baseMessage}</div>;
    }
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    ReactGA.event({
      category: "Enter_OTP_Continue",
      action: "Enter_OTP_Continue",
      label: "Enter_OTP_Continue",
    });
    setIsLoading(true);
    const payload = {
      id: encrypt(loginTypeData(otpUserData)),
      otp: encrypt(otp.join("")),
    };
    const registerPayload = {
      id: encrypt(mobileNumbers),
      otp: encrypt(otp.join("")),
    };
    const signupOnboardPayload = {
      lan: encrypt(lanNumbers),
      key: encrypt(mobileNumbers),
    };
    //verifyRegisterOtp
    if (registerKey == "register") {
      setIsLoading(true);
      service?.auth
        ?.verifyRegisterOtp(registerPayload)
        .then((res) => {
          const otpVerifyUsersData = res?.data?.data;
          setSeverity("success");
          // handleSnackbarOpen("OTP verification code sent", "success");
          dispatch(setOtpVerifyUsersData(otpVerifyUsersData));
          //  handleSnackbarOpen(`OTP sent to your`, "success");
          //service?.auth?.signupOnboard(signupOnboardPayload).then((res) => {
          service?.auth
            ?.signupOnboard(signupOnboardPayload)
            .then((res) => {
              let resData = res?.data?.data;
              const otpVerifyUsersData = decrypt(resData);
              dispatch(addSignupOnboardApi(otpVerifyUsersData));

              if (resData?.length > 1) {
                // Route to selectProfile page
                setIsLoading(true);
                setNextStep(ROUTES?.Login?.selectProfile?.path);
              } else {
                if (resData?.length == 0) {
                  const successMessage =
                    res?.data?.message || "An error occurred";
                  setIsLoading(false);
                  dispatch(
                    setStatusPopup({
                      status: "error",
                      message: successMessage,
                    })
                  );
                  // setNextStep(ROUTES?.Login?.register?.path);
                } else {
                  setNextStep(ROUTES?.Login?.thankYouPage?.path);
                }
              }
            })
            .catch((error: any) => {
              setSeverity("error");
              const errorMessage =
                error.response?.data?.errors?.[0]?.constraints?.matches || error?.response?.data?.message ||
                "An error occurred";
              setIsLoading(false);
              dispatch(
                setStatusPopup({
                  status: "error",
                  message: errorMessage,
                  isFunction: true,
                  locationFunction: handleBack,
                })
              );
            });
        })
        .catch((error) => {
          setSeverity("error");
          const errorMessage =
            error.response?.data?.message || "An error occurred";
          setIsLoading(false);
          dispatch(
            setStatusPopup({
              status: "error",
              message: errorMessage,
              isFunction: true,
              locationFunction: handleClearedOtp,
            })
          );
        });
    } else {
      service?.auth
        ?.verifyUserOtp(payload)
        .then((res) => {
          const otpVerifyUsersData = res?.data?.data;
          // console.log("otpVerifyUsersData", otpVerifyUsersData);

          const id = res?.data?.data?.[0]?.id;
          if (id) dispatch(setSetPasswordKey(id));
          dispatch(setSetPasswordArray(res?.data?.data));
          dispatch(setResetToken(res?.data?.resetToken));
          setSeverity("success");
          //  handleSnackbarOpen("OTP verification code sent", "success");
          dispatch(setOtpVerifyUsersData(otpVerifyUsersData));
          // handleSnackbarOpen(`OTP sent to your`, "success");
          if (otpVerifyUsersData?.length === 1) {
            dispatch(setUserProfileData(otpVerifyUsersData[0]));
            if (otpVerifyUsersData[0]?.isVerified) {
              setNextStep(ROUTES?.Login?.enterPassword?.path);
            } else {
              setNextStep(ROUTES?.Login?.setPassword?.path);
            }
          } else {
            setNextStep(ROUTES?.Login?.selectProfile?.path);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          setSeverity("error");
          const errorMessage =
            error.response?.data?.message || "An error occurred";
          setIsLoading(false);
          dispatch(
            setStatusPopup({
              status: "error",
              message: errorMessage,
              isFunction: true,
              locationFunction: handleClearedOtp,
            })
          );
        });
    }
  };

  const isComplete = otp.every((val) => val !== "");
  const handleBack = () => {
    if (registerKey == "register") {
      setNextStep(ROUTES?.Login?.register?.path);
    } else {
      setNextStep("");
    }
  };

  return (
    <Box>
      <Box className="main-wrapper" sx={{ display: "flex", alignItems: { xs: "unset !important" } }}>
        <Box className="head-section">
          <Box className="heading" sx={{ lineHeight: " 0.41px" }}>
            Enter OTP
          </Box>
          <Box className="sub-heading">
            {isTriggerDataEmpty ? (
              <>
                {userOtpMessage(otpUserData)}
              </>
            ) : (
              <>
                Please enter the OTP sent to your<span
                  style={{
                    color: "#1CB1B1",
                    textDecoration: "none",
                    WebkitTextDecorationLine: "none",
                  }}
                  className="text-disable wordbreak"
                >
                  {maskMobile(String(triggerData?.toUser ? triggerData?.applicantMobileNumber : triggerData?.coApplicantMobileNumber))}
                </span>
              </>
            )}

          </Box>
        </Box>
        <Box component="form">
          <Box className="sub-heading">Type your 6 digit security code</Box>
          <Grid
            container
            alignItems="center"
            justifyContent="center"
            spacing={2}
          >
            {otp?.map((value, index) => (
              <Grid item key={index} xs={2}>
                <InputFieldOTP
                  variant="outlined"
                  type="tel"
                  inputMode="decimal"
                  autoComplete="one-time-code"
                  width={isXsOrSm ? "38px" : "100%"}
                  autoFocus={index === 0}
                  value={value}
                  onChange={(
                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                  ) => handleOTPChange(index, e.target.value)}
                  onKeyUp={(e) => handleBackspaceAndEnter(e, index)}
                  inputProps={{ maxLength: 1 }}
                  inputRef={(el) =>
                    (inputRefs.current[index] = el as HTMLInputElement)
                  }
                />
              </Grid>
            ))}
          </Grid>
          <ResendOtpTimer
            minutes={3}
            resendOtpType="auth"
            resetPreviousOtp={handleOtpReset}
            isTriggerDataEmpty={isTriggerDataEmpty}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <PrimaryButton
              type="button"
              fullWidth
              onClick={
                isTriggerDataEmpty
                  ? handleSubmit
                  : triggerData?.toUser
                    ? handleSubmit
                    : handleVerifyCoApplicantOtp
              }
              disabled={!isComplete || isLoading}
            >
              <ButtonLoader title="Continue" isLoading={isLoading} />
            </PrimaryButton>
            <Box className="sub-heading">
              {registerKey === "register" || registerKey === undefined ? (
                <SecondaryButtonText onClick={handleBack}>
                  Change {loginType}
                </SecondaryButtonText>
              ) : (
                <SecondaryButtonText onClick={handleBack}>
                  Change{" "}
                  {otpUserData?.loginType === "cif"
                    ? "CIF"
                    : otpUserData?.loginType}
                </SecondaryButtonText>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Toast
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default Otp;
